import { Subscription } from "types/Models/subscription";
import Modal from "./DesignSystem/Modal/Modal";
import { TaxSubscriptionsListContent } from "pages/Tax/TaxSubscriptionModalContent";
import { CheckoutModal } from "./CheckoutModal/CheckoutModal";
import { useModal } from "hooks/useModal";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentYearIncorporate } from "hooks/UseCurrentYearIncorporate";
import { useAddProductToCartMutation } from "store/apis/products";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { BackendError } from "types/utils/error";
import { useToast } from "hooks/useToast";
import { useRoleBasedView } from "hooks/useRoleBasedView";

type TaxSoftwareSubscriptionsModalProps = {
  open: boolean;
  onClose: () => void;
  subscriptionPlans?: Subscription;
  onProductSubscribe?: () => void;
  isAddToCartFlow?: boolean;
};

export const TaxSoftwareSubscriptionsModal = ({
  open,
  onClose,
  subscriptionPlans,
  onProductSubscribe,
  isAddToCartFlow,
}: TaxSoftwareSubscriptionsModalProps) => {
  const checkoutModal = useModal();
  const entityId = useCurrentEntityId();
  const isScheduledPayment = useCurrentYearIncorporate();
  const [addProductToCart, { isLoading: addingProductToCart }] =
    useAddProductToCartMutation();
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const { isForeignCA } = useRoleBasedView();

  const onSubscribed = () => {
    checkoutModal.close();
    onClose();
    onProductSubscribe?.();
  };

  const addTaxToCart = async () => {
    try {
      if (subscriptionPlans && entityId && groupId) {
        const { product_content_type_id, uuid } = subscriptionPlans;

        const payload = {
          product_content_type_id,
          product_id: uuid,
          quantity: 1,
        };

        await addProductToCart({
          groupId: groupId,
          entityId,
          payload: payload,
        }).unwrap();
        successToast({ title: "Item added!" });
        onClose();
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content size="large">
        <Modal.Header>
          <div className="t-w-full">
            <Modal.Title titleIcon={<Modal.Close />}>
              <div>Upgrade Your Plan </div>
            </Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div>
            <TaxSubscriptionsListContent
              price={subscriptionPlans?.amount}
              type={subscriptionPlans?.subscription_name}
              features={subscriptionPlans?.metadata?.comparison || []}
              description={
                subscriptionPlans?.metadata?.service_description || ""
              }
              onUpgrade={() => {
                checkoutModal.open();
              }}
              isFreePlanActive={!subscriptionPlans?.group_subscription}
              isAddToCartFlow={isAddToCartFlow}
              handleAddToCart={addTaxToCart}
              isLoading={addingProductToCart}
            />
            {subscriptionPlans && entityId && (
              <CheckoutModal
                entityId={entityId}
                type="subscription"
                onClose={checkoutModal.close}
                open={checkoutModal.isOpen}
                subscription={subscriptionPlans!}
                onSubscribed={onSubscribed}
                {...(isScheduledPayment && { title: "Schedule Payment" })}
                isScheduledPayment={isScheduledPayment}
                payerUserType={isForeignCA && "FCA"}
              />
            )}
          </div>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
